import { Component, inject } from '@angular/core';
import { toDataURL } from 'qrcode';
import { environment } from '@et/environment';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { TwoFactorEnrollmentService } from '../../services/two-factor-enrollment.service';

/**
 * Post-login 2FA enrollment surface. Enable/disable 2FA and manage backup codes.
 * HTTP calls are delegated to `TwoFactorEnrollmentService` (shared with
 * `TwoFactorRequiredComponent`, the mandatory blocking enrollment gate); this
 * component owns only the enroll/activate UI state.
 *
 * Two-step activation: `/two-factor/enable` (server, `skipVerificationOnEnable`
 * removed) only ever returns `{ totpURI, backupCodes }` — it never flips
 * `user.twoFactorEnabled`. `confirmActivation` performs the required
 * `verify-totp` (default) or `send-otp`+`verify-otp` round trip while the full
 * session from `enable()` is still live. Mirrors
 * `better-auth-standalong/scripts/complete-two-factor-enrollment.ts`.
 */
@Component({
  selector: 'auth-two-factor-settings',
  templateUrl: './two-factor-settings.component.html',
  standalone: true,
  imports: [PresentationalAtomsModule],
})
export class TwoFactorSettingsComponent {
  private enrollment = inject(TwoFactorEnrollmentService);

  enabled = false;
  pendingActivation = false;
  activationMethod: 'totp' | 'otp' = 'totp';
  backupCodes: string[] = [];
  totpURI?: string;
  qrDataUrl?: string;
  error = false;
  twoFactorEnabled = environment.betterAuth?.twoFactorEnabled ?? false;
  totpEnabled = environment.betterAuth?.totpEnabled ?? false;

  async enable(password: string): Promise<void> {
    this.error = false;
    try {
      const res = await this.enrollment.enable(password);
      this.backupCodes = res?.backupCodes ?? [];
      this.totpURI = res?.totpURI;
      this.qrDataUrl = this.totpURI ? await toDataURL(this.totpURI) : undefined;
      // enable() never activates — a real verify round trip is required next.
      this.pendingActivation = true;
    } catch {
      this.error = true;
    }
  }

  /**
   * Completes activation. `code` is a live TOTP code (authenticator app) by
   * default, or the emailed/SMS OTP when `activationMethod === 'otp'`.
   */
  async confirmActivation(code: string): Promise<void> {
    this.error = false;
    try {
      if (this.activationMethod === 'otp') {
        await this.enrollment.verifyOtp(code);
      } else {
        await this.enrollment.verifyTotp(code);
      }
      this.enabled = true;
      this.pendingActivation = false;
    } catch {
      this.error = true;
    }
  }

  /** For `activationMethod === 'otp'`: requests the emailed/SMS code to confirm with. */
  async sendActivationOtp(): Promise<void> {
    this.error = false;
    try {
      await this.enrollment.sendOtp();
    } catch {
      this.error = true;
    }
  }

  async disable(password: string): Promise<void> {
    this.error = false;
    try {
      await this.enrollment.disable(password);
      this.enabled = false;
      this.pendingActivation = false;
      this.backupCodes = [];
      this.totpURI = undefined;
      this.qrDataUrl = undefined;
    } catch {
      this.error = true;
    }
  }

  async regenerateBackupCodes(password: string): Promise<void> {
    this.error = false;
    try {
      const res = await this.enrollment.regenerateBackupCodes(password);
      this.backupCodes = res?.backupCodes ?? [];
    } catch {
      this.error = true;
    }
  }
}
